/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useEffect } from 'react'
import { captureException } from "../../../../constant/sentryConfiguration";
import { useNavigate } from 'react-router-dom';
import { StateContext } from '../../../../context/globalContext/context';
import { iGlobalContext } from '../../../../context/globalContext/interface';
import { iHistoryContext } from '../../../../context/historyContext/interface';
import { useHistoryContext } from '../../../../context/historyContext/historyContext';
import { usePixelEvent } from '../../../../hooks/usePixelEvent';
import { useNavigationUrl } from '../../../../hooks/useNavigationUrl';
import { ROUTES } from '../../../../constant';
import { confirmBookHotelAndFlight } from '../../../../api/booking/booking';
import { gaEventsCustomParamGetSelectedFlightDetails, gaEventsCustomParamGetSelectedHotelDetails } from '../../../../constant/google-analytics/utils/gaEventsCustomParam';
import { GA_LABEL_ENUMS, triggerGAEvent } from '../../../../constant/google-analytics/GAEvents';
import { useTheresiasEventBridge } from '../../../../services/intent/useTheresiasEventBridge';
import { iChatData } from '../../../../types';
import { INITIAL_BREADCRUMBS_LIST } from '../../../../constant/bookingInProgressConstant';
import { confirmHintForId } from '../../../../utils/payment/checkoutHandoff';
import { isEmbedMode } from "../../../../utils/embedMode";
import { getBookingIdempotencyKeyField } from "../../../../utils/booking/bookingIdempotencyKey";
import { getPickSource } from '../../../../utils/booking/pickSourceStore';

// How long a confirm claim blocks a repeat POST of the same payment: longer
// than a confirm can take (API Gateway cuts requests at ~29s), so a reload
// during a slow confirm never re-sends it, yet a dead attempt can be retried.
const CONFIRM_CLAIM_WINDOW_MS = 2 * 60 * 1000;

function useConfirmBookingAPi(
    setIsBookingFailed: any,
    setIsLoading: any,
    selectedChatDataIdRef: any,
    session_Id: any,
    setBookingInprogressFlag: any,
    noPayment: boolean = false,
    toBookedChatData?: (inProgress: boolean) => iChatData[],
) {
    const state = useContext(StateContext);
    const {
        selectedChatData,
        selectedFlightOptions,
        bookedtransIds,
        setBookedTransIds,
        setTripOrItinearyId,
        userInfo,
        setFlightData,
        setHotelData,
        accessJWT,
        setSelectedHotelRoomArr,
        setBookingDetails,
        setChatData,
        chatData,
        setSelectedChatData,
        setItineraryId,
        promoReviewCartData,
        setPromoCode,
        bookingDetails,
        setActiveBreadCrumbs
    }: iGlobalContext = state;
    const { saveHistory, getHistory }: iHistoryContext = useHistoryContext();
    const { sendPixelEvent } = usePixelEvent();
    const fireTheresiasEvent = useTheresiasEventBridge();
    const { itineraryID = "", flightTransactionId = "", hotelTransactionId = "", activityTransactionId = "" } = bookedtransIds || {};
    const { navigateWithParams } = useNavigationUrl();
    const navigate = useNavigate();

    useEffect(() => {
        setActiveBreadCrumbs(INITIAL_BREADCRUMBS_LIST[0])
    }, [])

    function clearCart() {
        setSelectedHotelRoomArr([]);
        setBookingDetails((prev: any) => ({ ...prev, selectedFlightId: "" }));
        setFlightData(null);
        setHotelData(null);
    }

    const confirmBookingAPI = useCallback(
        async ({
            bookedHotelTransactionId,
            bookedFlightTransactionId,
            bookedItineraryId,
            startDate,
            endDate,
            numberOfDaysSelected,
        }: {
            bookedHotelTransactionId: string;
            bookedFlightTransactionId: string;
            bookedItineraryId: string;
            startDate: string;
            endDate: string;
            numberOfDaysSelected: number;
        }) => {
            setPromoCode([]);
            setIsBookingFailed(false);
            if (window && window.parent) {
                const message = {
                    action: "beforePrebooking",
                    data: {},
                };
                window.parent.postMessage(JSON.stringify(message));
            }

            await new Promise((resolve) => setTimeout(resolve, 400));
            if (!flightTransactionId && !hotelTransactionId && !activityTransactionId) {
                if (!isEmbedMode()) navigateWithParams(ROUTES.Home);
                return;
            }
            // One confirm per payment. A reload/remount of /paymentsuccess while
            // the first confirm was still in flight re-ran this effect and POSTed
            // the same payment twice (double booking, 2026-07-22). The claim is
            // keyed by the payment (wallet: by this prebook's idempotency key),
            // so it lives in localStorage and also covers a second tab. It
            // records the itinerary it was taken for and when:
            // - another itinerary: an older success page reached from history,
            //   which must never confirm the current booking against its payment;
            // - same itinerary, within CONFIRM_CLAIM_WINDOW_MS: the first confirm
            //   may still be running, so show it in progress and don't POST again;
            // - same itinerary, older: that attempt is over, so confirm again. It
            //   carries the same Idempotency-Key, which lets the server dedupe it.
            const claimId =
                session_Id || `nopay_${getBookingIdempotencyKeyField().idempotencyKey || bookedItineraryId}`;
            const confirmClaimKey = `_zenvoya_booking_confirm_${claimId}`;
            let claim: { itineraryId?: string; at?: number } | null = null;
            try {
                claim = JSON.parse(localStorage.getItem(confirmClaimKey) || "null");
            } catch {}
            if (claim && claim.itineraryId !== bookedItineraryId) {
                if (!isEmbedMode()) navigateWithParams(ROUTES.Home, { replace: true });
                return;
            }
            if (claim && Date.now() - (claim.at || 0) < CONFIRM_CLAIM_WINDOW_MS) {
                setBookingInprogressFlag(true);
                setIsLoading(false);
                return;
            }
            try {
                localStorage.setItem(confirmClaimKey, JSON.stringify({ itineraryId: bookedItineraryId, at: Date.now() }));
            } catch {}
            setTripOrItinearyId(selectedChatData?.id);
            // Store UUID in ref so "Go to trip" can use it even if selectedChatData gets cleared
            if (selectedChatData?.id) {
                selectedChatDataIdRef.current = selectedChatData.id;
            }
            const hint = noPayment ? null : confirmHintForId(session_Id);
            // eslint-disable-next-line no-console
            console.log("[checkout-handoff] confirm hint", {
                session_Id: session_Id || "(none)",
                noPayment,
                sends: hint ? `${hint.field}=${hint.value}` : "(no hint — wallet HELD)",
            });
            setIsLoading(true);
            let response = await confirmBookHotelAndFlight(
                accessJWT,
                {
                    itineraryId: bookedItineraryId,
                    userId: userInfo?.id,
                    id: selectedChatData?.id || selectedChatDataIdRef.current,

                    ...(hint ? { [hint.field]: hint.value } : {}),
                }
                // setIsLoading
            );
            // response = {
            //   ...response,
            //   status: "inprogress"
            // }
            setIsLoading(false);
            if (response) {
                let customGAParams: any = {};
                if (selectedFlightOptions?.flights && selectedFlightOptions?.hotels) {
                    customGAParams = {
                        price: promoReviewCartData?.price?.totalAmountAfterDiscount ?? 0,

                        ...gaEventsCustomParamGetSelectedFlightDetails(bookingDetails),
                        ...gaEventsCustomParamGetSelectedHotelDetails(bookingDetails),
                    };
                } else if (selectedFlightOptions?.flights) {
                    customGAParams = {
                        price: promoReviewCartData?.price?.totalAmountAfterDiscount ?? 0,
                        ...gaEventsCustomParamGetSelectedFlightDetails(bookingDetails),
                    };
                } else if (selectedFlightOptions?.hotels) {
                    customGAParams = {
                        price: promoReviewCartData?.price?.totalAmountAfterDiscount ?? 0,
                        ...gaEventsCustomParamGetSelectedHotelDetails(bookingDetails),
                    };
                }
                if (response?.status === "inprogress") setBookingInprogressFlag(true);
                triggerGAEvent(
                    GA_LABEL_ENUMS.booking_success,
                    "P:" +
                    promoReviewCartData?.price?.totalAmountAfterDiscount +
                    "|T:" +
                    (selectedFlightOptions?.flights && selectedFlightOptions?.hotels
                        ? "F/S"
                        : selectedFlightOptions?.flights
                            ? "F"
                            : "S"),
                    "",
                    "",
                    customGAParams
                );
                fireTheresiasEvent("payment-pay-success", customGAParams);
                sendPixelEvent("paymentComplete", {
                    event: GA_LABEL_ENUMS.booking_success,
                    value: Number(promoReviewCartData?.price?.totalAmountAfterDiscount) || 0,
                    currency: "USD",
                });
                // const metaData: any = {
                //   selectedChatData: {
                //     ...selectedChatData,
                //     destinationList: updateDaywiseTripDateForMetaData(selectedChatData?.destinationList, startDate),
                //     prompt: [],
                //     postText: "",
                //     preText: "",
                //     isBooked: true,
                //   },
                // };
                if (!bookedItineraryId) return;
                if (!flightTransactionId && !hotelTransactionId && !activityTransactionId) return;
                setBookedTransIds({
                    ...bookedtransIds,
                    flightTransactionId: "",
                    hotelTransactionId: "",
                    activityTransactionId: "",
                });
                clearCart();
                /**
                 * Update the history with booked flag
                 */
                // A chat-flight / chat-hotel STANDALONE booking does NOT correspond to the
                // itinerary in `selectedChatData` — that's just whatever trip the user last
                // opened (e.g. they viewed the London itinerary, went back, then searched &
                // booked a SEPARATE flight; `selectedChatData` is never re-pointed by the
                // flight-card flow). The id-match below would then wrongly stamp isBooked:true
                // onto that unrelated itinerary, and a booked card is blocked from opening in
                // chat (SelectedTripNew.handleCardClick -> the `isBooked && !isBookingFailed`
                // render guard in TopDestination). Only stamp the card for genuine itinerary
                // bookings (where no chat-flight/hotel marker is set and selectedChatData IS
                // the booked trip).
                const isStandaloneChatBooking =
                    getPickSource("flight") === "chat" ||
                    getPickSource("hotel") === "chat";
                const tempChatData = chatData.map((ele: iChatData) => {
                    if (!isStandaloneChatBooking && ele?.id === selectedChatData?.id) {
                        setSelectedChatData((prev: any) => ({
                            ...prev,
                            isBooked: true,
                        }));
                        return {
                            ...ele,
                            isBooked: true,
                        };
                    }
                    return ele;
                });
                const savedChatData = toBookedChatData
                    ? toBookedChatData(response?.status === "inprogress")
                    : tempChatData;
                setChatData(savedChatData);
                await saveHistory(savedChatData, "PUT");
                await getHistory(true);
                // // handleViewItinerary();
                // if (selectedFlightOptions?.flights && !selectedFlightOptions?.hotels) {
                //   return;
                // }
                // if (selectedFlightOptions?.hotels && !selectedFlightOptions?.flights) {
                //   return;
                // }
                // if (selectedFlightOptions?.hotels && selectedFlightOptions?.flights) {
                // }
                // } else {
                //   triggerGAEvent(GA_LABEL_ENUMS.booking_failure);
                //   setBookedTransIds({
                //     ...bookedtransIds,
                //     flightTransactionId: "",
                //     hotelTransactionId: "",
                //   });
                //   setIsBookingFailed(true);
                //   if (saveItineraryDetailsResponse?.message) throw new Error(saveItineraryDetailsResponse?.message);
                // }
                setIsLoading(false);
            }
            if (!response) {
                captureException({
                    error: "BOOKING CONFIRMATION ERROR",
                    response: response,
                });
                triggerGAEvent(GA_LABEL_ENUMS.booking_failure);
                // const metaData: any = {
                //     selectedChatData: {
                //         ...selectedChatData,
                //         destinationList: updateDaywiseTripDateForMetaData(selectedChatData?.destinationList, startDate),
                //         prompt: [],
                //         postText: "",
                //         preText: "",
                //         isBooked: true,
                //         isBookingFailed: true,
                //     },
                // };
                if (!bookedItineraryId) return;
                if (!flightTransactionId && !hotelTransactionId && !activityTransactionId) return;

                const tempChatData = chatData.map((ele: iChatData) => {
                    if (ele?.id === selectedChatData?.id) {
                        setSelectedChatData((prev: any) => ({
                            ...prev,
                            isBooked: true,
                            isBookingFailed: true,
                        }));
                        return {
                            ...ele,
                            isBooked: true,
                            isBookingFailed: true,
                        };
                    }
                    return ele;
                });
                setChatData(tempChatData);
                await saveHistory(tempChatData, "PUT");
                await getHistory(true);
                setBookedTransIds({
                    ...bookedtransIds,
                    flightTransactionId: "",
                    hotelTransactionId: "",
                    activityTransactionId: "",
                });
                setIsBookingFailed(true);
            }
            setItineraryId(itineraryID);
            setIsLoading(false);
        },
        [
            accessJWT,
            bookedtransIds,
            clearCart,
            flightTransactionId,
            // handleViewItinerary,
            hotelTransactionId,
            navigate,
            selectedChatData,
            selectedFlightOptions?.flights,
            selectedFlightOptions?.hotels,
            session_Id,
            setBookedTransIds,
            userInfo?.id,
            selectedChatData,
            chatData,
        ]
    );
    return {
        confirmBookingAPI,
    }
}

export default useConfirmBookingAPi